import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ShieldCheckIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline'

const Disclaimer = () => {
    const disclaimers = [
        {
            title: 'Educational Information',
            icon: InformationCircleIcon,
            content: 'The information provided on this website is for general educational purposes only. While we strive to provide accurate and up-to-date information, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the information contained on this website.',
            color: 'text-blue-600 bg-blue-100'
        },
        {
            title: 'University Partnerships',
            icon: ShieldCheckIcon,
            content: 'MyMargdarshak acts as an intermediary between students and educational institutions. We do not guarantee admission to any university or institution. Admission decisions are solely made by the respective universities based on their criteria and requirements.',
            color: 'text-green-600 bg-green-100'
        },
        {
            title: 'Course Information',
            icon: DocumentTextIcon,
            content: 'Course details, fees, duration, and curriculum information are subject to change without notice. Students are advised to verify all information directly with the respective universities before making any decisions.',
            color: 'text-purple-600 bg-purple-100'
        },
        {
            title: 'Career Guidance',
            icon: ExclamationTriangleIcon,
            content: 'Career guidance and counseling services are provided based on general market trends and industry insights. Individual results may vary, and we do not guarantee specific career outcomes or job placements.',
            color: 'text-orange-600 bg-orange-100'
        }
    ]

    const legalPoints = [
        'This website and its content are provided "as is" without any warranties.',
        'MyMargdarshak shall not be liable for any direct, indirect, incidental, or consequential damages.',
        'Users are responsible for verifying all information before making decisions.',
        'We reserve the right to modify or discontinue services without notice.',
        'Third-party links and content are not under our control.',
        'Intellectual property rights are respected and protected.'
    ]

    return (
        <>
            <Helmet>
                <title>Disclaimer | MyMargdarshak</title>
                <meta name="description" content="Read our disclaimer regarding educational information, university partnerships, course details, and career guidance services provided by MyMargdarshak." />
                <meta name="keywords" content="disclaimer, terms, educational information, university partnerships, career guidance" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 flex flex-col pt-0">
                {/* Modern Professional Legal Hero */}
                <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden bg-white border-b border-gray-100">
                    {/* Subtle Background Pattern */}
                    <div className="absolute inset-0 z-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
                    
                    {/* Floating Design Element */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] pointer-events-none overflow-hidden">
                        <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-50 rounded-full blur-[100px] opacity-60"></div>
                        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-50 rounded-full blur-[80px] opacity-60"></div>
                    </div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full mb-6 border border-blue-100 italic"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span className="text-xs font-black uppercase tracking-widest">Legal Document</span>
                            </motion.div>

                            <motion.h1 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-4xl sm:text-6xl font-black mb-6 text-gray-900 leading-tight tracking-tight"
                            >
                                <span className="text-primary-600">Disclaimer</span>
                            </motion.h1>

                            <motion.p 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto font-medium"
                            >
                                Please read this disclaimer carefully to understand our information policies and limitations.
                            </motion.p>
                        </div>
                    </div>
                </section>

                {/* Disclaimer Content */}
                <section className="py-16">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Introduction */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="bg-white rounded-xl shadow-soft p-8 mb-12"
                        >
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">General Disclaimer</h2>
                            <p className="text-gray-600 leading-relaxed">
                                Welcome to MyMargdarshak. This disclaimer governs your use of our website and services.
                                By using our website, you accept this disclaimer in full. If you disagree with any part
                                of this disclaimer, you must not use our website.
                            </p>
                        </motion.div>

                        {/* Detailed Disclaimers */}
                        <div className="space-y-8 mb-12">
                            {disclaimers.map((disclaimer, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="bg-white rounded-xl shadow-soft p-8"
                                >
                                    <div className="flex items-start space-x-4">
                                        <div className={`p-3 rounded-lg ${disclaimer.color}`}>
                                            <disclaimer.icon className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold text-gray-900 mb-3">
                                                {disclaimer.title}
                                            </h3>
                                            <p className="text-gray-600 leading-relaxed">
                                                {disclaimer.content}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Legal Points */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="bg-white rounded-xl shadow-soft p-8 mb-12"
                        >
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Important Legal Points</h2>
                            <ul className="space-y-3">
                                {legalPoints.map((point, index) => (
                                    <li key={index} className="flex items-start space-x-3">
                                        <div className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <span className="text-gray-600">{point}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Contact Information */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                            className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-8 text-center"
                        >
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About This Disclaimer?</h2>
                            <p className="text-gray-600 mb-6">
                                If you have any questions about this disclaimer or our services, please don't hesitate to contact us.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <a
                                    href="/contact"
                                    className="btn-primary"
                                >
                                    Contact Us
                                </a>
                                <a
                                    href="/terms"
                                    className="btn-outline"
                                >
                                    Terms of Service
                                </a>
                            </div>
                        </motion.div>

                        {/* Last Updated */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.6 }}
                            className="text-center text-gray-500 text-sm mt-8"
                        >
                            <p>Last updated: {new Date().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}</p>
                        </motion.div>
                    </div>
                </section>
            </div>
        </>
    )
}

export default Disclaimer
